import { useState, useEffect, useCallback, useRef } from "react";
import {
  X, Send, Paperclip, MessageCircle, ThumbsUp, MessageSquare,
  Trash2, Download, File, FileText, FileSpreadsheet, ImageIcon,
  User, Truck, CreditCard, Calendar, Award, Hash,
  StickyNote, Clock, ShoppingBag, BarChart2
} from "lucide-react";
import API_BASE_URL from "../config";

// ─── CONSTANTS ────────────────────────────────────────────────
const ALLOWED_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const REACTIONS = [
  { emoji: "👍", label: "Like", color: "#3B82F6", bg: "#EFF6FF" },
  { emoji: "❤️", label: "Love", color: "#EF4444", bg: "#FEF2F2" },
  { emoji: "😂", label: "Haha", color: "#F59E0B", bg: "#FFFBEB" },
  { emoji: "😮", label: "Wow", color: "#8B5CF6", bg: "#F5F3FF" },
  { emoji: "😢", label: "Sad", color: "#6B7280", bg: "#F9FAFB" },
  { emoji: "😡", label: "Angry", color: "#EF4444", bg: "#FEF2F2" },
];

const AVATAR_COLORS = [
  { from: "#6366F1", to: "#8B5CF6" },
  { from: "#EC4899", to: "#F43F5E" },
  { from: "#14B8A6", to: "#06B6D4" },
  { from: "#F59E0B", to: "#EF4444" },
  { from: "#10B981", to: "#3B82F6" },
  { from: "#8B5CF6", to: "#EC4899" },
  { from: "#06B6D4", to: "#10B981" },
];

const QUOT_STATUS_MAP = {
  0: { label: "Draft", dot: "#94A3B8", color: "#64748B", bg: "#F8FAFC" },
  1: { label: "Submitted", dot: "#3B82F6", color: "#1D4ED8", bg: "#EFF6FF" },
  2: { label: "Awarded", dot: "#10B981", color: "#065F46", bg: "#ECFDF5" },
  3: { label: "Rejected", dot: "#EF4444", color: "#991B1B", bg: "#FEF2F2" },
};

// ─── PROFILE CACHE (module-level, never inside component) ──────
const PROFILE_CACHE_KEY = "afm_profileCache_v2";
const CACHE_EXPIRY_MS = 60 * 60 * 1000;

const getProfileCache = () => {
  try { return JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY) || "{}"); }
  catch { return {}; }
};
const setProfileCache = (name, url) => {
  try {
    const c = getProfileCache();
    c[name] = { url, ts: Date.now() };
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(c));
  } catch { }
};
const getProfileFromCache = (name) => {
  try {
    const e = getProfileCache()[name];
    if (!e || Date.now() - e.ts > CACHE_EXPIRY_MS) return null;
    return e.url;
  } catch { return null; }
};

// ─── HELPERS ──────────────────────────────────────────────────
const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTimeAgo = (dateString) => {
  const diff = (new Date() - new Date(dateString)) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const getFileIcon = (type) => {
  if (!type) return <File size={14} />;
  if (type.startsWith("image/")) return <ImageIcon size={14} />;
  if (type === "application/pdf") return <FileText size={14} />;
  if (type.includes("word")) return <FileText size={14} />;
  if (type.includes("sheet")) return <FileSpreadsheet size={14} />;
  return <File size={14} />;
};

const getAvatarColor = (name) =>
  AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

// ─── STYLES ───────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  .afm-root { font-family: 'DM Sans', sans-serif; }
  .afm-mono { font-family: 'DM Mono', monospace; }

  .afm-backdrop { animation: afmFadeIn 0.2s ease; }
  .afm-modal    { animation: afmSlideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }

  @keyframes afmFadeIn  { from { opacity:0 } to { opacity:1 } }
  @keyframes afmSlideUp {
    from { opacity:0; transform: translateY(24px) scale(0.97); }
    to   { opacity:1; transform: translateY(0)    scale(1);    }
  }

  .afm-feed-item { animation: afmFeedIn 0.35s ease both; }
  @keyframes afmFeedIn {
    from { opacity:0; transform:translateY(10px); }
    to   { opacity:1; transform:translateY(0);    }
  }

  .afm-reaction-picker {
    transition: opacity 0.15s ease, transform 0.2s cubic-bezier(.34,1.56,.64,1);
  }
  .afm-reaction-btn { transition: transform 0.15s cubic-bezier(.34,1.56,.64,1); }
  .afm-reaction-btn:hover { transform: scale(1.5) translateY(-4px); }

  .afm-float {
    position:fixed; font-size:1.5rem; pointer-events:none;
    animation: afmFloat 0.7s ease forwards; z-index:9999;
  }
  @keyframes afmFloat {
    from { opacity:1; transform:translateY(0)     scale(1);   }
    to   { opacity:0; transform:translateY(-60px) scale(1.5); }
  }

  @keyframes likePop {
    0%   { transform: scale(1);   }
    50%  { transform: scale(1.4); }
    100% { transform: scale(1);   }
  }
  .afm-like-pop { animation: likePop 0.3s cubic-bezier(.34,1.56,.64,1); }

  .afm-send-btn { transition: all 0.15s ease; }
  .afm-send-btn:not(:disabled):hover { transform: translateX(2px); }

  .afm-post-box { transition: box-shadow 0.2s ease, border-color 0.2s ease; }
  .afm-post-box:focus-within {
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
    border-color: #6366F1 !important;
  }

  .afm-comment-input:focus {
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
    border-color: #6366F1 !important;
    outline: none;
  }

  .afm-scrollbar::-webkit-scrollbar { width:4px; }
  .afm-scrollbar::-webkit-scrollbar-track { background:transparent; }
  .afm-scrollbar::-webkit-scrollbar-thumb { background:#E2E8F0; border-radius:99px; }
  .afm-scrollbar::-webkit-scrollbar-thumb:hover { background:#CBD5E1; }

  .afm-shimmer {
    background: linear-gradient(90deg,#F8FAFC 25%,#F1F5F9 50%,#F8FAFC 75%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
  }
  @keyframes shimmer {
    from { background-position:200%  0; }
    to   { background-position:-200% 0; }
  }

  .afm-comment-row:hover .afm-del-comment { opacity: 1 !important; }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;

// ─── SPAWN FLOAT EMOJI ────────────────────────────────────────
const spawnFloat = (emoji, x, y) => {
  const el = document.createElement("span");
  el.className = "afm-float";
  el.textContent = emoji;
  el.style.left = (x - 16) + "px";
  el.style.top = (y - 16) + "px";
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 700);
};

// ─── AVATAR ───────────────────────────────────────────────────
const UserAvatar = ({ lookupKey, name, size = "md", userProfiles }) => {
  const profile = lookupKey ? userProfiles[lookupKey] : undefined;
  const initial = (name || "?")[0].toUpperCase();
  const { from, to } = getAvatarColor(name);
  const sz = size === "sm" ? 28 : size === "xs" ? 22 : 38;
  const fs = size === "sm" ? "0.65rem" : size === "xs" ? "0.55rem" : "0.85rem";

  const resolvedUrl = (() => {
    const url = profile?.profilePictureUrl || profile?.avatarUrl || profile?.photo;
    if (!url) return null;
    return url.startsWith("http") ? url : `${API_BASE_URL}${url}`;
  })();

  const style = {
    width: sz, height: sz, borderRadius: "50%", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: fs, fontWeight: 700, color: "white",
    background: `linear-gradient(135deg,${from},${to})`,
    boxShadow: "0 0 0 2px white, 0 0 0 3px rgba(99,102,241,0.2)",
  };

  if (resolvedUrl) {
    return (
      <img
        src={resolvedUrl}
        alt={name || "User"}
        style={{ ...style, objectFit: "cover", background: "none" }}
        onError={e => {
          e.currentTarget.style.display = "none";
          const el = document.createElement("div");
          Object.assign(el.style, style);
          el.textContent = initial;
          e.currentTarget.parentNode?.insertBefore(el, e.currentTarget.nextSibling);
        }}
      />
    );
  }
  return <div style={style}>{initial}</div>;
};

// ─── REACTION PICKER ──────────────────────────────────────────
const ReactionPicker = ({ feedId, myReaction, onReact }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const timer = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const myData = REACTIONS.find(r => r.emoji === myReaction?.reactionType);

  const handleReact = (emoji) => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      spawnFloat(emoji, rect.left + rect.width / 2, rect.top);
      btnRef.current.classList.remove("afm-like-pop");
      void btnRef.current.offsetWidth;
      btnRef.current.classList.add("afm-like-pop");
    }
    onReact(feedId, emoji);
    setOpen(false);
  };

  // Toggle off if same emoji; otherwise react with 👍
  const handleLikeClick = () => {
    handleReact(myData ? myData.emoji : "👍");
  };

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(true), 280); }}
      onMouseLeave={() => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 350); }}
    >
      {/* Picker popup */}
      <div className="afm-reaction-picker" style={{
        position: "absolute", bottom: "calc(100% + 6px)", left: 0, zIndex: 200,
        display: "flex", alignItems: "center", gap: 2,
        background: "white", borderRadius: 999, padding: "6px 8px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #F1F5F9",
        opacity: open ? 1 : 0,
        transform: open ? "scale(1) translateY(0)" : "scale(0.6) translateY(8px)",
        pointerEvents: open ? "auto" : "none",
        transformOrigin: "bottom left",
        whiteSpace: "nowrap",
      }}>
        {REACTIONS.map(r => (
          <button
            key={r.emoji}
            className="afm-reaction-btn"
            style={{
              background: "none", border: "none", cursor: "pointer", padding: "2px 4px",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 2
            }}
            onClick={() => handleReact(r.emoji)}
            onMouseEnter={() => setHovered(r.emoji)}
            onMouseLeave={() => setHovered(null)}
          >
            <span style={{ fontSize: "1.6rem", lineHeight: 1, fontFamily: "'Noto Color Emoji',sans-serif" }}>
              {r.emoji}
            </span>
            {hovered === r.emoji && (
              <span style={{
                fontSize: "0.55rem", fontWeight: 700, color: "#6366F1",
                background: "#EFF6FF", borderRadius: 99, padding: "1px 5px", letterSpacing: "0.02em"
              }}>
                {r.label}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Main like button */}
      <button
        ref={btnRef}
        onClick={handleLikeClick}
        style={{
          display: "flex", alignItems: "center", gap: 5, padding: "6px 12px",
          borderRadius: 8, border: "none", background: "none", cursor: "pointer",
          fontSize: "0.78rem", fontWeight: 600, fontFamily: "inherit",
          color: myData ? myData.color : "#64748B", transition: "background 0.15s ease"
        }}
        onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
        onMouseLeave={e => e.currentTarget.style.background = "none"}
      >
        {myData
          ? <span style={{ fontSize: "1rem", lineHeight: 1 }}>{myData.emoji}</span>
          : <ThumbsUp size={14} />
        }
        {myData ? myData.label : "Like"}
      </button>
    </div>
  );
};

// ─── SKELETON ─────────────────────────────────────────────────
const FeedSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    {[1, 2, 3].map(i => (
      <div key={i} style={{
        background: "white", borderRadius: 16, padding: 20,
        border: "1px solid #F1F5F9", overflow: "hidden"
      }}>
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div className="afm-shimmer" style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <div className="afm-shimmer" style={{ height: 12, width: "40%", borderRadius: 6 }} />
            <div className="afm-shimmer" style={{ height: 10, width: "25%", borderRadius: 6 }} />
          </div>
        </div>
        <div className="afm-shimmer" style={{ height: 10, width: "80%", borderRadius: 6, marginBottom: 6 }} />
        <div className="afm-shimmer" style={{ height: 10, width: "60%", borderRadius: 6 }} />
      </div>
    ))}
  </div>
);

// ─── SIDEBAR INFO ROW ─────────────────────────────────────────
const InfoRow = ({ label, value, icon: Icon }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 3, padding: "10px 14px",
      background: "white", borderRadius: 12, border: "1px solid #F1F5F9"
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 5,
        fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.06em",
        textTransform: "uppercase", color: "#94A3B8"
      }}>
        {Icon && <Icon size={10} />} {label}
      </div>
      <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#1E293B", wordBreak: "break-word" }}>
        {value}
      </div>
    </div>
  );
};

const StarRow = ({ value = 0 }) => (
  <div style={{ display: "flex", gap: 2 }}>
    {[1, 2, 3, 4, 5].map(s => (
      <span key={s} style={{ fontSize: "0.9rem", color: s <= value ? "#F59E0B" : "#E2E8F0" }}>★</span>
    ))}
  </div>
);

// ─── SPINNER ──────────────────────────────────────────────────
const Spinner = () => (
  <span style={{
    width: 12, height: 12, borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid white",
    display: "inline-block",
    animation: "spin 0.8s linear infinite",
  }} />
);

// ─── MAIN COMPONENT ───────────────────────────────────────────
export default function ActivityFeedModal({ request, onClose }) {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [posting, setPosting] = useState(false);
  const [commentInputs, setCommentInputs] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});

  const fileInputRef = useRef(null);
  const feedEndRef = useRef(null);
  const fetchedNames = useRef(new Set());
  const textareaRef = useRef(null);

  // Read user once
  const userRef = useRef(JSON.parse(localStorage.getItem("user") || "{}"));
  const currentUserId = parseInt(userRef.current.userId);
  const currentName = userRef.current.fullName || userRef.current.username || "You";

  // ── FIX: use request.id consistently for quotation feeds ────
  // The backend GET endpoint is /quotation/{quotationId}
  // and POST uses quotationId — both must use the same value.
  // We derive it once here so nothing drifts.
  const quotationId = request.quotationId ?? request.id;

  // ── Fetch feeds ──────────────────────────────────────────────
  const fetchFeeds = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}api/ActivityFeed/quotation/${quotationId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFeeds(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchFeeds error:", err);
    } finally {
      setLoading(false);
    }
  }, [quotationId]);

  useEffect(() => { fetchFeeds(); }, [fetchFeeds]);

  // ── Fetch avatars ────────────────────────────────────────────
  useEffect(() => {
    const names = [...new Set([
      currentName,
      ...feeds.flatMap(f => [
        f.createdByName,
        ...(f.comments || []).map(c => c.createdByName),
      ]),
    ].filter(Boolean))];

    names.forEach(name => {
      if (fetchedNames.current.has(name)) return;
      fetchedNames.current.add(name);
      const cached = getProfileFromCache(name);
      const url = cached || `${API_BASE_URL}api/Users/username/${encodeURIComponent(name)}/profile`;
      if (!cached) setProfileCache(name, url);
      setUserProfiles(p => ({ ...p, [name]: { profilePictureUrl: url } }));
    });
  }, [feeds]);

  // ── File validation ──────────────────────────────────────────
  const validateAndSetFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert("File type not allowed.\nAllowed: Images, PDF, DOCX, XLSX");
      return;
    }
    if (file.size > 10 * 1024 * 1024) { alert("File too large. Max 10MB."); return; }
    setSelectedFile(file);
  };

  // ── Post ─────────────────────────────────────────────────────
  const handlePost = async () => {
    if (!newPost.trim() && !selectedFile) return;
    setPosting(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/ActivityFeed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quotationId,          // ← FIX: consistent with fetchFeeds
          prHeaderId: null,
          content: newPost.trim() || `Uploaded: ${selectedFile?.name}`,
          feedType: selectedFile ? "file" : "text",
          createdBy: currentUserId,
          createdByName: currentName,
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        console.error("Post failed:", res.status, errText);
        alert(`Failed to create post (${res.status}):\n${errText}`);
        return;
      }

      const created = await res.json();

      if (selectedFile && created?.id) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", selectedFile);
        const uploadRes = await fetch(
          `${API_BASE_URL}api/ActivityFeed/${created.id}/upload`,
          { method: "POST", body: fd }
        );
        if (!uploadRes.ok) {
          console.error("Upload failed:", await uploadRes.text());
        }
        setUploading(false);
      }

      setNewPost("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchFeeds();
      setTimeout(() => feedEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      console.error("handlePost error:", err);
      alert("Error: " + err.message);
    } finally {
      setPosting(false);
      setUploading(false);
    }
  };

  // ── Comment ──────────────────────────────────────────────────
  const handleComment = async (feedId) => {
    const text = commentInputs[feedId];
    if (!text?.trim()) return;

    setSubmittingComment(p => ({ ...p, [feedId]: true }));
    try {
      const res = await fetch(
        `${API_BASE_URL}api/ActivityFeed/${feedId}/comment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            comment: text.trim(),
            createdBy: currentUserId,
            createdByName: currentName,
          }),
        }
      );

      if (!res.ok) {
        // ── FIX: surface the real backend error ─────────────
        const errText = await res.text();
        console.error("Comment API error:", res.status, errText);
        alert(`Comment failed (${res.status}):\n${errText}`);
        return;
      }

      setCommentInputs(p => ({ ...p, [feedId]: "" }));
      await fetchFeeds();
    } catch (err) {
      console.error("handleComment error:", err);
    } finally {
      setSubmittingComment(p => ({ ...p, [feedId]: false }));
    }
  };

  // ── React ─────────────────────────────────────────────────────
  const handleReact = async (feedId, reactionType) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}api/ActivityFeed/${feedId}/react`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: currentUserId,
            userName: currentName,
            reactionType,
          }),
        }
      );
      if (!res.ok) {
        console.error("React failed:", res.status, await res.text());
        return;
      }
      await fetchFeeds();
    } catch (err) {
      console.error("handleReact error:", err);
    }
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDeleteFeed = async (feedId) => {
    if (!window.confirm("Delete this post?")) return;
    await fetch(`${API_BASE_URL}api/ActivityFeed/${feedId}`, { method: "DELETE" });
    await fetchFeeds();
  };

  const handleDeleteComment = async (commentId) => {
    await fetch(`${API_BASE_URL}api/ActivityFeed/comment/${commentId}`, { method: "DELETE" });
    await fetchFeeds();
  };

  const totalReactions = feeds.reduce((a, f) => a + (f.reactions?.length || 0), 0);
  const totalComments = feeds.reduce((a, f) => a + (f.comments?.length || 0), 0);
  const statusInfo = QUOT_STATUS_MAP[request.quotStatus] || QUOT_STATUS_MAP[0];

  // ─── RENDER ──────────────────────────────────────────────────
  return (
    <>
      <style>{STYLES}</style>

      {/* Backdrop */}
      <div
        className="afm-backdrop"
        style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        {/* Modal */}
        <div
          className="afm-root afm-modal"
          style={{
            width: "100%", maxWidth: 920, height: "min(88vh,720px)",
            background: "#F8FAFC", borderRadius: 24, overflow: "hidden",
            boxShadow: "0 32px 96px rgba(15,23,42,0.3), 0 0 0 1px rgba(255,255,255,0.1)",
            display: "flex", flexDirection: "column",
          }}
        >
          {/* ── HEADER ─────────────────────────────────────── */}
          <div style={{
            padding: "18px 24px",
            background: "linear-gradient(135deg,#4F46E5  )",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexShrink: 0, position: "relative", overflow: "hidden",
          }}>
            {/* decorative circles */}
            <div style={{
              position: "absolute", top: -20, right: 80, width: 100, height: 100,
              background: "rgba(255,255,255,0.06)", borderRadius: "50%", pointerEvents: "none"
            }} />
            <div style={{
              position: "absolute", bottom: -30, right: 20, width: 80, height: 80,
              background: "rgba(255,255,255,0.04)", borderRadius: "50%", pointerEvents: "none"
            }} />

            <div style={{ display: "flex", alignItems: "center", gap: 14, zIndex: 1 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12,
                background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)", flexShrink: 0,
              }}>
                <MessageCircle size={20} color="white" />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
                  <h2 style={{ margin: 0, fontWeight: 700, fontSize: "1rem", color: "white", letterSpacing: "-0.01em" }}>
                    Activity Feed
                  </h2>
                  <span style={{
                    background: "rgba(255,255,255,0.2)", color: "white",
                    fontSize: "0.65rem", fontWeight: 700, padding: "2px 8px",
                    borderRadius: 99, letterSpacing: "0.04em",
                  }}>
                    {feeds.length} POSTS
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="afm-mono" style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.75rem" }}>
                    {request.prNo}
                  </span>
                  {request.supplierName && (
                    <>
                      <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.65rem" }}>·</span>
                      <span style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.75rem" }}>
                        {request.supplierName}
                      </span>
                    </>
                  )}
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: "rgba(255,255,255,0.15)", color: "white",
                    fontSize: "0.65rem", fontWeight: 600, padding: "2px 8px", borderRadius: 99,
                  }}>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: statusInfo.dot, flexShrink: 0 }} />
                    {statusInfo.label}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, zIndex: 1 }}>
              {[{ icon: "💬", val: totalComments, tip: "Comments" },
              { icon: "👍", val: totalReactions, tip: "Reactions" }].map(s => (
                <div key={s.tip} style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  background: "rgba(255,255,255,0.12)", borderRadius: 10, padding: "6px 12px",
                }}>
                  <span style={{ fontSize: "1rem", lineHeight: 1 }}>{s.icon}</span>
                  <span style={{ color: "white", fontWeight: 700, fontSize: "0.7rem" }}>{s.val}</span>
                </div>
              ))}
              <button
                onClick={onClose}
                style={{
                  width: 34, height: 34, borderRadius: 10,
                  background: "rgba(255,255,255,0.12)", border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "rgba(255,255,255,0.8)", marginLeft: 4, transition: "background 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* ── BODY ───────────────────────────────────────── */}
          <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>

            {/* LEFT: feed column */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>

              {/* Composer */}
              <div style={{
                padding: "16px 20px", borderBottom: "1px solid #E2E8F0",
                background: "white", flexShrink: 0
              }}>
                <div
                  className="afm-post-box"
                  style={{
                    border: "1.5px solid #E2E8F0", borderRadius: 16, overflow: "hidden",
                    background: "#FAFBFF",
                    ...(isDragging ? {
                      background: "linear-gradient(135deg,#EFF6FF,#F5F3FF)",
                      borderColor: "#6366F1", borderStyle: "dashed",
                    } : {}),
                  }}
                  onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={e => {
                    e.preventDefault(); setIsDragging(false);
                    const f = e.dataTransfer.files[0]; if (f) validateAndSetFile(f);
                  }}
                >
                  {isDragging ? (
                    <div style={{ padding: "28px 20px", textAlign: "center" }}>
                      <div style={{ fontSize: "2rem", marginBottom: 8 }}>📎</div>
                      <p style={{ margin: 0, color: "#6366F1", fontWeight: 600, fontSize: "0.85rem" }}>
                        Drop your file here
                      </p>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: 12, padding: "14px 16px 10px" }}>
                        <UserAvatar lookupKey={currentName} name={currentName} size="md" userProfiles={userProfiles} />
                        <textarea
                          ref={textareaRef}
                          rows={2}
                          placeholder={`Share an update on ${request.prNo}...`}
                          value={newPost}
                          onChange={e => setNewPost(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) handlePost(); }}
                          style={{
                            flex: 1, border: "none", background: "transparent", resize: "none",
                            fontFamily: "inherit", fontSize: "0.875rem", color: "#1E293B",
                            outline: "none", lineHeight: 1.6, padding: 0,
                          }}
                        />
                      </div>

                      {selectedFile && (
                        <div style={{
                          margin: "0 16px 10px", display: "flex", alignItems: "center", gap: 10,
                          background: "#EFF6FF", borderRadius: 10, padding: "8px 12px",
                          border: "1px solid #BFDBFE",
                        }}>
                          <span style={{ color: "#3B82F6" }}>{getFileIcon(selectedFile.type)}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: "0.78rem", fontWeight: 600, color: "#1D4ED8",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                            }}>
                              {selectedFile.name}
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "#60A5FA" }}>
                              {formatFileSize(selectedFile.size)}
                            </div>
                          </div>
                          <button
                            onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                            style={{
                              background: "none", border: "none", cursor: "pointer",
                              color: "#93C5FD", padding: 4, borderRadius: 6,
                              display: "flex", transition: "color 0.1s"
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = "#EF4444"}
                            onMouseLeave={e => e.currentTarget.style.color = "#93C5FD"}
                          >
                            <X size={13} />
                          </button>
                        </div>
                      )}

                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "8px 16px 12px", borderTop: "1px solid #F1F5F9",
                      }}>
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "none", border: "1px solid #E2E8F0", borderRadius: 8,
                            padding: "5px 12px", cursor: "pointer", color: "#64748B",
                            fontSize: "0.75rem", fontWeight: 600, fontFamily: "inherit", transition: "all 0.15s",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = "#6366F1"; e.currentTarget.style.color = "#6366F1"; e.currentTarget.style.background = "#EFF6FF"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.color = "#64748B"; e.currentTarget.style.background = "none"; }}
                        >
                          <Paperclip size={12} /> Attach file
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*,.pdf,.docx,.xlsx"
                          style={{ display: "none" }}
                          onChange={e => { const f = e.target.files[0]; if (f) validateAndSetFile(f); }}
                        />

                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: "0.7rem", color: "#CBD5E1" }}>Ctrl+Enter</span>
                          <button
                            className="afm-send-btn"
                            onClick={handlePost}
                            disabled={posting || uploading || (!newPost.trim() && !selectedFile)}
                            style={{
                              display: "flex", alignItems: "center", gap: 7,
                              background: "linear-gradient(135deg,#6366F1,#8B5CF6)",
                              color: "white", border: "none", borderRadius: 10,
                              padding: "8px 18px", cursor: "pointer",
                              fontSize: "0.8rem", fontWeight: 700, fontFamily: "inherit",
                              boxShadow: "0 4px 12px rgba(99,102,241,0.35)",
                              opacity: (posting || uploading || (!newPost.trim() && !selectedFile)) ? 0.45 : 1,
                            }}
                          >
                            {uploading ? <><Spinner /> Uploading</>
                              : posting ? <><Spinner /> Posting</>
                                : <><Send size={13} /> Post</>}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Feed list */}
              <div
                className="afm-scrollbar"
                style={{
                  flex: 1, overflowY: "auto", padding: "16px 20px",
                  display: "flex", flexDirection: "column", gap: 14
                }}
              >
                {loading ? <FeedSkeleton /> : feeds.length === 0 ? (
                  <div style={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    justifyContent: "center", flex: 1, padding: "60px 20px", color: "#94A3B8",
                  }}>
                    <div style={{ fontSize: "3rem", marginBottom: 12, opacity: 0.5 }}>💬</div>
                    <p style={{ margin: "0 0 4px", fontWeight: 600, fontSize: "0.9rem", color: "#CBD5E1" }}>
                      No activity yet
                    </p>
                    <p style={{ margin: 0, fontSize: "0.8rem" }}>Be the first to post an update!</p>
                  </div>
                ) : feeds.map((feed, feedIdx) => {
                  const reactionMap = {};
                  (feed.reactions || []).forEach(r => {
                    reactionMap[r.reactionType] = (reactionMap[r.reactionType] || 0) + 1;
                  });
                  const myReaction = (feed.reactions || []).find(r => r.userId === currentUserId);
                  const isMe = feed.createdBy === currentUserId;

                  return (
                    <div
                      key={feed.id}
                      className="afm-feed-item"
                      style={{
                        animationDelay: `${feedIdx * 40}ms`,
                        background: "white", borderRadius: 18, overflow: "visible",  // ← PALITAN
                        border: "1px solid #F1F5F9",
                        boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
                        transition: "box-shadow 0.2s ease",
                      }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(15,23,42,0.09)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 4px rgba(15,23,42,0.05)"}
                    >
                      {/* Feed header */}
                      {/* Feed header */}
                      <div style={{ display: "flex", gap: 12, padding: "14px 16px 10px", alignItems: "flex-start" }}>
                        <UserAvatar lookupKey={feed.createdByName} name={feed.createdByName} size="md" userProfiles={userProfiles} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#1E293B" }}>
                                {feed.createdByName || "Unknown"}
                              </span>
                              <span style={{
                                fontSize: "0.72rem", color: "#94A3B8",
                                display: "flex", alignItems: "center", gap: 3
                              }}>
                                <Clock size={10} /> {formatTimeAgo(feed.createdAt)}
                              </span>
                              {feed.feedType === "file" && (
                                <span style={{
                                  fontSize: "0.68rem", fontWeight: 600, color: "#6366F1",
                                  background: "#EFF6FF", borderRadius: 99, padding: "1px 8px",
                                  display: "flex", alignItems: "center", gap: 4, border: "1px solid #C7D2FE",
                                }}>
                                  <Paperclip size={9} /> with file
                                </span>
                              )}
                            </div>
                            {isMe && (
                              <button
                                onClick={() => handleDeleteFeed(feed.id)}
                                style={{
                                  background: "none", border: "none", cursor: "pointer",
                                  color: "#CBD5E1", borderRadius: 7, padding: 5,
                                  display: "flex", transition: "all 0.15s"
                                }}
                                onMouseEnter={e => { e.currentTarget.style.color = "#EF4444"; e.currentTarget.style.background = "#FEF2F2"; }}
                                onMouseLeave={e => { e.currentTarget.style.color = "#CBD5E1"; e.currentTarget.style.background = "none"; }}
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                          <p style={{ margin: 0, fontSize: "0.875rem", color: "#374151", lineHeight: 1.65 }}>
                            {feed.content}
                          </p>

                          {/* ✅ BADGES — laging visible kahit may attachment */}
                          {((feed.reactions || []).length > 0 || (feed.comments || []).length > 0) && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                              {(feed.reactions || []).length > 0 && (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: "#EFF6FF", borderRadius: 99, padding: "3px 10px",
                                  fontSize: "0.72rem", fontWeight: 700, color: "#6366F1",
                                  border: "1px solid #C7D2FE",
                                }}>
                                  👍 {(feed.reactions || []).length}
                                </span>
                              )}
                              {(feed.comments || []).length > 0 && (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: "#F5F3FF", borderRadius: 99, padding: "3px 10px",
                                  fontSize: "0.72rem", fontWeight: 700, color: "#8B5CF6",
                                  border: "1px solid #DDD6FE",
                                }}>
                                  💬 {(feed.comments || []).length}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Attachments */}
                      {(feed.attachments || []).length > 0 && (
                        <div style={{ padding: "0 16px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                          {feed.attachments.map(att => {
                            const isImg = att.fileType?.startsWith("image/");
                            const url = `${API_BASE_URL}api/ActivityFeed/attachment/file/${att.id}`;
                            return isImg ? (
                              <div key={att.id} style={{
                                borderRadius: 12, overflow: "hidden",
                                border: "1px solid #E0E7FF", background: "#F8FAFF"
                              }}>
                                <img
                                  src={url} alt={att.fileName}
                                  style={{
                                    width: "100%", maxHeight: 240, objectFit: "contain",
                                    cursor: "pointer", display: "block", transition: "opacity 0.15s"
                                  }}
                                  onClick={() => window.open(url, "_blank")}
                                  onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                                  onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                />
                                <div style={{
                                  display: "flex", alignItems: "center", justifyContent: "space-between",
                                  padding: "8px 12px", background: "#F0F4FF", borderTop: "1px solid #E0E7FF"
                                }}>
                                  <div>
                                    <div style={{
                                      fontSize: "0.75rem", fontWeight: 600, color: "#4338CA",
                                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200
                                    }}>
                                      {att.fileName}
                                    </div>
                                    <div style={{ fontSize: "0.68rem", color: "#818CF8" }}>
                                      {formatFileSize(att.fileSize)}
                                    </div>
                                  </div>
                                  <a
                                    href={url} download={att.fileName}
                                    style={{
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      width: 28, height: 28, borderRadius: 8, background: "#E0E7FF",
                                      color: "#6366F1", textDecoration: "none", transition: "all 0.15s"
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = "#6366F1"; e.currentTarget.style.color = "white"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = "#E0E7FF"; e.currentTarget.style.color = "#6366F1"; }}
                                  >
                                    <Download size={13} />
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <a
                                key={att.id} href={url} target="_blank" rel="noreferrer"
                                style={{
                                  display: "flex", alignItems: "center", gap: 10,
                                  background: "linear-gradient(135deg,#EFF6FF,#F5F3FF)",
                                  border: "1px solid #C7D2FE", borderRadius: 12,
                                  padding: "10px 14px", textDecoration: "none", transition: "all 0.15s",
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = "#6366F1"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(99,102,241,0.15)"; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = "#C7D2FE"; e.currentTarget.style.boxShadow = "none"; }}
                              >
                                <span style={{ color: "#6366F1" }}>{getFileIcon(att.fileType)}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{
                                    fontSize: "0.78rem", fontWeight: 600, color: "#4338CA",
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                                  }}>
                                    {att.fileName}
                                  </div>
                                  <div style={{ fontSize: "0.7rem", color: "#818CF8" }}>
                                    {formatFileSize(att.fileSize)}
                                  </div>
                                </div>
                                <Download size={14} style={{ color: "#818CF8", flexShrink: 0 }} />
                              </a>
                            );
                          })}
                        </div>
                      )}

                      {/* Reaction pills */}
                      {/* Reaction pills — laging visible */}


                      {/* Action bar */}
                      {/* Action bar — Like + Comment buttons + reaction pills */}
                      <div style={{
                        padding: "6px 12px", borderTop: "1px solid #F1F5F9",
                        display: "flex", alignItems: "center",
                        justifyContent: "space-between", background: "white",
                      }}>
                        {/* Left: Like + Comment buttons */}
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <ReactionPicker feedId={feed.id} myReaction={myReaction} onReact={handleReact} />
                          <button
                            onClick={() => { }}
                            style={{
                              display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
                              borderRadius: 8, border: "none", background: "none", cursor: "pointer",
                              fontSize: "0.8rem", fontWeight: 700, fontFamily: "inherit",
                              color: (feed.comments || []).length > 0 ? "#6366F1" : "#64748B",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}
                          >
                            <MessageSquare size={14} />
                            {(feed.comments || []).length > 0
                              ? `${(feed.comments || []).length} Comment${(feed.comments || []).length !== 1 ? "s" : ""}`
                              : "Comment"
                            }
                          </button>
                        </div>

                        {/* Right: Reaction emoji pills */}
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {Object.entries(reactionMap).map(([emoji, count]) => {
                            const r = REACTIONS.find(x => x.emoji === emoji);
                            return (
                              <span key={emoji} style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                background: r?.bg || "#F8FAFC",
                                border: `1px solid ${r?.color || "#E2E8F0"}44`,
                                borderRadius: 99, padding: "3px 10px",
                                fontSize: "0.75rem", fontWeight: 700,
                                color: r?.color || "#64748B",
                              }}>
                                <span style={{ fontSize: "0.9rem" }}>{emoji}</span> {count}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Comments list */}
                      {(feed.comments || []).length > 0 && (
                        <div style={{
                          background: "#FAFBFF", borderTop: "1px solid #F1F5F9",
                          padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10
                        }}>
                          {feed.comments.map(c => (
                            <div key={c.id} className="afm-comment-row"
                              style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                              <UserAvatar lookupKey={c.createdByName} name={c.createdByName}
                                size="sm" userProfiles={userProfiles} />
                              <div style={{
                                flex: 1, background: "white", borderRadius: 12,
                                padding: "8px 12px", border: "1px solid #F1F5F9",
                                boxShadow: "0 1px 3px rgba(15,23,42,0.04)"
                              }}>
                                <span style={{ fontWeight: 700, fontSize: "0.75rem", color: "#374151" }}>
                                  {c.createdByName}{" "}
                                </span>
                                <span style={{ fontSize: "0.82rem", color: "#4B5563" }}>{c.comment}</span>
                                <div style={{ fontSize: "0.68rem", color: "#94A3B8", marginTop: 3 }}>
                                  {formatTimeAgo(c.createdAt)}
                                </div>
                              </div>
                              {c.createdBy === currentUserId && (
                                <button
                                  className="afm-del-comment"
                                  onClick={() => handleDeleteComment(c.id)}
                                  style={{
                                    background: "none", border: "none", cursor: "pointer",
                                    color: "#E2E8F0", padding: "6px 4px", borderRadius: 6,
                                    opacity: 0, transition: "all 0.15s", marginTop: 2,
                                    display: "flex", alignItems: "center",
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.color = "#EF4444"}
                                  onMouseLeave={e => e.currentTarget.style.color = "#E2E8F0"}
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add comment */}
                      <div style={{
                        padding: "10px 16px 14px", display: "flex", gap: 9, alignItems: "center",
                        borderTop: (feed.comments || []).length > 0 ? "1px solid #F1F5F9" : "none",
                        background: (feed.comments || []).length > 0 ? "#FAFBFF" : "white",
                      }}>
                        <UserAvatar lookupKey={currentName} name={currentName}
                          size="sm" userProfiles={userProfiles} />
                        <input
                          type="text"
                          placeholder="Add a comment..."
                          value={commentInputs[feed.id] || ""}
                          onChange={e => setCommentInputs(p => ({ ...p, [feed.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === "Enter") handleComment(feed.id); }}
                          className="afm-comment-input"
                          style={{
                            flex: 1, border: "1.5px solid #E2E8F0", borderRadius: 99,
                            padding: "7px 16px", fontSize: "0.8rem", fontFamily: "inherit",
                            background: "white", color: "#1E293B", outline: "none",
                            transition: "border-color 0.15s, box-shadow 0.15s",
                          }}
                        />
                        <button
                          onClick={() => handleComment(feed.id)}
                          disabled={submittingComment[feed.id] || !commentInputs[feed.id]?.trim()}
                          style={{
                            width: 32, height: 32, borderRadius: "50%", border: "none",
                            background: "linear-gradient(135deg,#6366F1,#8B5CF6)",
                            color: "white", cursor: "pointer", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(99,102,241,0.3)",
                            opacity: (submittingComment[feed.id] || !commentInputs[feed.id]?.trim()) ? 0.4 : 1,
                            transition: "opacity 0.15s, transform 0.15s",
                          }}
                          onMouseEnter={e => { if (!e.currentTarget.disabled) e.currentTarget.style.transform = "scale(1.1)"; }}
                          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                        >
                          <Send size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                <div ref={feedEndRef} />
              </div>
            </div>

            {/* RIGHT: Sidebar */}
            <div
              className="afm-scrollbar"
              style={{
                width: 248, flexShrink: 0, borderLeft: "1px solid #E2E8F0",
                background: "#F8FAFC", overflowY: "auto", display: "flex", flexDirection: "column",
              }}
            >
              {/* Grand total */}
              <div style={{
                margin: 14, borderRadius: 16,
                background: "linear-gradient(135deg,#065F46,#059669)",
                padding: 16, textAlign: "center",
                boxShadow: "0 8px 24px rgba(5,150,105,0.28)",
              }}>
                <div style={{
                  fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em",
                  textTransform: "uppercase", color: "rgba(255,255,255,0.65)", marginBottom: 4
                }}>
                  Grand Total
                </div>
                <div className="afm-mono" style={{
                  fontWeight: 800, fontSize: "1.25rem",
                  color: "white", letterSpacing: "-0.01em"
                }}>
                  {request.grandTotal != null
                    ? `₱${Number(request.grandTotal).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`
                    : "—"}
                </div>
                {request.quotationNo && (
                  <div style={{
                    marginTop: 8, fontSize: "0.68rem", color: "rgba(255,255,255,0.6)",
                    fontFamily: "DM Mono, monospace"
                  }}>
                    {request.quotationNo}
                  </div>
                )}
              </div>

              {/* Status badge */}
              {request.quotStatus != null && (() => {
                const st = QUOT_STATUS_MAP[request.quotStatus] || QUOT_STATUS_MAP[0];
                return (
                  <div style={{ margin: "-4px 14px 14px", display: "flex", justifyContent: "center" }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: st.bg, color: st.color, fontSize: "0.72rem", fontWeight: 700,
                      padding: "5px 14px", borderRadius: 99,
                      border: `1px solid ${st.dot}44`,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: st.dot, flexShrink: 0 }} />
                      {st.label}
                      {request.isAwarded && <><Award size={11} /> Awarded</>}
                    </span>
                  </div>
                );
              })()}

              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "0 14px 14px" }}>
                {[
                  { val: feeds.length, label: "Posts", emoji: "📝", color: "#6366F1" },
                  { val: totalComments, label: "Comments", emoji: "💬", color: "#8B5CF6" },
                  { val: totalReactions, label: "Reactions", emoji: "👍", color: "#EC4899" },
                  { val: feeds.filter(f => f.attachments?.length).length, label: "Files", emoji: "📎", color: "#14B8A6" },
                ].map(s => (
                  <div key={s.label} style={{
                    background: "white", borderRadius: 12, padding: "10px 8px",
                    textAlign: "center", border: "1px solid #F1F5F9",
                    boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
                  }}>
                    <div style={{ fontSize: "1rem", marginBottom: 2 }}>{s.emoji}</div>
                    <div style={{ fontSize: "1.15rem", fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</div>
                    <div style={{ fontSize: "0.62rem", fontWeight: 600, color: "#94A3B8", marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ margin: "0 14px 14px", height: 1, background: "#E2E8F0" }} />

              {/* Quotation details */}
              <div style={{ padding: "0 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{
                  fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em",
                  textTransform: "uppercase", color: "#94A3B8", marginBottom: 2
                }}>
                  Quotation Details
                </div>
                <InfoRow label="Supplier" value={request.supplierName} icon={User} />
                <InfoRow label="PR Reference" value={request.prNo} icon={Hash} />
                <InfoRow label="Delivery"
                  value={request.deliveryDays ? `${request.deliveryDays} days` : null}
                  icon={Truck} />
                <InfoRow label="Payment Terms" value={request.paymentTerms} icon={CreditCard} />
                <InfoRow label="Valid Until" value={formatDate(request.validUntil)} icon={Calendar} />
                <InfoRow label="Date Created" value={formatDate(request.createdAt)} icon={Clock} />

                {request.supplierRating != null && request.supplierRating > 0 && (
                  <div style={{
                    display: "flex", flexDirection: "column", gap: 4, padding: "10px 14px",
                    background: "white", borderRadius: 12, border: "1px solid #F1F5F9"
                  }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 5,
                      fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.06em",
                      textTransform: "uppercase", color: "#94A3B8"
                    }}>
                      <BarChart2 size={10} /> Supplier Rating
                    </div>
                    <StarRow value={request.supplierRating} />
                  </div>
                )}

                {request.itemCount != null && (
                  <InfoRow label="Items Quoted"
                    value={`${request.itemCount} item${request.itemCount !== 1 ? "s" : ""}`}
                    icon={ShoppingBag} />
                )}

                {request.remarks && (
                  <div style={{
                    background: "#FFFBEB", border: "1px solid #FDE68A",
                    borderRadius: 12, padding: "10px 14px", marginTop: 2
                  }}>
                    <div style={{
                      display: "flex", alignItems: "center", gap: 5, fontSize: "0.65rem",
                      fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                      color: "#92400E", marginBottom: 6
                    }}>
                      <StickyNote size={10} /> Remarks
                    </div>
                    <p style={{ margin: 0, fontSize: "0.78rem", color: "#78350F", lineHeight: 1.6 }}>
                      {request.remarks}
                    </p>
                  </div>
                )}

                <div style={{
                  background: "white", border: "1px solid #F1F5F9", borderRadius: 12,
                  padding: "10px 14px", marginBottom: 14
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 5, fontSize: "0.65rem",
                    fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                    color: "#94A3B8", marginBottom: 8
                  }}>
                    <Paperclip size={10} /> Allowed Files
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {[
                      { label: "Images", icon: ImageIcon },
                      { label: "PDF", icon: FileText },
                      { label: "DOCX", icon: FileText },
                      { label: "XLSX", icon: FileSpreadsheet },
                    ].map(({ label, icon: Icon }) => (
                      <span key={label} style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        background: "#EFF6FF", color: "#4F46E5", fontSize: "0.67rem",
                        fontWeight: 600, padding: "3px 8px", borderRadius: 99,
                        border: "1px solid #C7D2FE",
                      }}>
                        <Icon size={9} /> {label}
                      </span>
                    ))}
                  </div>
                  <p style={{ margin: "8px 0 0", fontSize: "0.68rem", color: "#94A3B8" }}>Max 10MB per file</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}